import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  buscarComposicao,
  buscarEmpresasProjetaveis,
  buscarProjecaoEmpresa,
  buscarProjecaoGrupo,
} from "./api";
import type {
  ComposicaoOut,
  EmpresaProjetavelOut,
  ProjecaoEmpresaOut,
  ProjecaoGrupoOut,
} from "./tipos";

/*
 * Hooks de server-state da projeção (FE-2 CP-A — ADR 0026 §Estado / TanStack
 * Query). Toda leitura de servidor passa pelo Query: cache, dedup e revalidação
 * com a config conservadora do `criarQueryClient` (staleTime 30s, retry 1).
 *
 * Convenções de queryKey: prefixo `"projecao"` + recurso + parâmetros que
 * afetam o resultado (empresaId, horizonte). Chaves estáveis garantem cache hit
 * e refetch correto ao trocar empresa/horizonte.
 *
 * `enabled` (gate de disparo): impede o fetch enquanto o parâmetro essencial não
 * existe (empresa não selecionada) ou o caller não autorizou (grupo só para
 * usuário irrestrito — o CP-C liga via `habilitado`). Sem `enabled`, a query
 * dispararia com `null`/sem permissão e geraria um 4xx desnecessário.
 */

/** Empresas projetáveis pelo escopo do usuário. */
export function useEmpresasProjetaveis(): UseQueryResult<
  EmpresaProjetavelOut[]
> {
  return useQuery({
    queryKey: ["projecao", "empresas"],
    queryFn: buscarEmpresasProjetaveis,
  });
}

/**
 * Projeção de uma empresa. Só dispara quando há empresa selecionada
 * (`empresaId !== null`) — antes disso a query fica ociosa (`enabled: false`),
 * sem fetch nem erro. A queryKey inclui empresaId+horizonte: trocar qualquer um
 * é uma query distinta no cache.
 */
export function useProjecaoEmpresa(
  empresaId: string | null,
  horizonte: number,
): UseQueryResult<ProjecaoEmpresaOut> {
  return useQuery({
    queryKey: ["projecao", "empresa", empresaId, horizonte],
    // `empresaId!` é seguro: `enabled` garante que a queryFn só roda com empresa
    // não-nula. O TanStack só invoca a queryFn quando `enabled` é true.
    queryFn: () => buscarProjecaoEmpresa(empresaId as string, horizonte),
    enabled: empresaId !== null,
  });
}

/**
 * Projeção do grupo. Só dispara quando `habilitado` é true — o CP-C liga apenas
 * para usuário com projeção IRRESTRITA (ver `../auth/permissoes`). A queryKey
 * inclui horizonte: trocar o horizonte refaz a query.
 */
export function useProjecaoGrupo(
  horizonte: number,
  habilitado: boolean,
): UseQueryResult<ProjecaoGrupoOut> {
  return useQuery({
    queryKey: ["projecao", "grupo", horizonte],
    queryFn: () => buscarProjecaoGrupo(horizonte),
    enabled: habilitado,
  });
}

/**
 * Composição itemizada de um dia (drill-down da tela 2). Só dispara quando há um
 * dia selecionado (`data !== null`) — antes disso a query fica ociosa
 * (`enabled: false`), sem fetch nem erro (não há drill-down sem dia clicado). A
 * queryKey inclui empresaId+data+horizonte: cada combinação é uma query distinta.
 */
export function useComposicao(
  empresaId: string,
  data: string | null,
  horizonte: number,
): UseQueryResult<ComposicaoOut> {
  return useQuery({
    queryKey: ["projecao", "composicao", empresaId, data, horizonte],
    // `data!` é seguro: `enabled` garante que a queryFn só roda com `data`
    // não-nula. O TanStack só invoca a queryFn quando `enabled` é true.
    queryFn: () => buscarComposicao(empresaId, data as string, horizonte),
    enabled: data !== null,
  });
}
